import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useChangeLanguage } from "@/hooks/useChangeLanguage";
import { useStravaSync } from "@/hooks/useStravaSync";
import type { AppLocale } from "@/i18n/locales";
import { supabase } from "@/lib/supabase";
import { sessionAtom, userAtom } from "@/state/auth.state";
import { localeAtom } from "@/state/locale.state";
import { syncLastSyncedAtAtom } from "@/state/sync.state";
import { unitSystemAtom, type UnitSystem } from "@/state/units.state";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useAtom, useAtomValue } from "jotai";
import { CloudSync, EllipsisVertical, LogOut, Trash2 } from "lucide-react";
import * as React from "react";
import { ResponsiveMenu } from "../responsive/ResponsiveMenu";
import { UserSummary } from "./UserSummary";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

function getInitials(input: string) {
  const s = input.trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).filter(Boolean).slice(0, 2);
  const chars = parts.map((p) => p[0]?.toUpperCase()).filter(Boolean);
  return (chars.join("") || s[0]!.toUpperCase()).slice(0, 2);
}

export function Profile() {
  const { isMobile } = useSidebar();
  const [session, setSession] = useAtom(sessionAtom);
  const user = useAtomValue(userAtom);
  const syncLastSyncedAt = useAtomValue(syncLastSyncedAtAtom);

  const LANGUAGE_LABEL: Record<AppLocale, string> = {
    en: "English",
    fr: "Français",
  };

  const locale = useAtomValue(localeAtom);
  const changeLanguage = useChangeLanguage();

  const UNIT_LABEL: Record<UnitSystem, string> = {
    metric: t`Metric`,
    imperial: t`Imperial`,
  };

  const [unitSystem, setUnitSystem] = useAtom(unitSystemAtom);

  async function changeUnitSystem(next: UnitSystem) {
    if (next === unitSystem) return;
    setUnitSystem(next);
  }

  const { startSync } = useStravaSync();

  const { name, avatarUrl, initials } = React.useMemo(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;

    const metaName =
      (meta.name as string | undefined) ??
      (meta.full_name as string | undefined);

    const displayName = metaName?.trim() || user?.email || t`User`;

    const avatarUrl = meta.strava_athlete_avatar_url as string | undefined;

    return {
      name: displayName,
      avatarUrl,
      initials: getInitials(displayName),
    };
  }, [user]);

  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  async function signOut() {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut();
    } finally {
      setIsSigningOut(false);
    }
  }

  async function deleteAccount() {
    if (isDeleting || !session) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      setSession(null);
      try {
        await signOut();
      } catch (_e) {
        // ignore
      }
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : t`Failed to delete account. Try again.`;
      setDeleteError(msg);
      setIsDeleting(false);
    }
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <ResponsiveMenu
            isMobile={isMobile}
            trigger={
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                aria-label={t`Open profile menu`}
              >
                <UserSummary
                  name={name}
                  avatarUrl={avatarUrl}
                  initials={initials}
                  lastSync={syncLastSyncedAt}
                  className="flex-1"
                />
                <EllipsisVertical
                  className="ml-auto size-4"
                  aria-hidden="true"
                />
              </SidebarMenuButton>
            }
            pages={[
              {
                id: "root",
                title: <Trans>Profile</Trans>,
                items: [
                  {
                    type: "label",
                    key: "summary",
                    content: (
                      <UserSummary
                        name={name}
                        avatarUrl={avatarUrl}
                        initials={initials}
                        lastSync={syncLastSyncedAt}
                        className="px-1 py-1.5"
                      />
                    ),
                  },
                  { type: "separator", key: "sep-1" },

                  {
                    type: "page",
                    key: "lang",
                    label: <Trans>Language</Trans>,
                    pageId: "language",
                  },

                  {
                    type: "page",
                    key: "units",
                    label: <Trans>Units</Trans>,
                    pageId: "units",
                  },

                  { type: "separator", key: "sep-2" },

                  {
                    type: "action",
                    key: "resync",
                    label: <Trans>Resync</Trans>,
                    icon: <CloudSync className="h-4 w-4" />,
                    disabled: isSigningOut || isDeleting,
                    onSelect: () => {
                      startSync({ mode: "force_fetch" });
                    },
                  },
                  {
                    type: "action",
                    key: "logout",
                    label: isSigningOut ? (
                      <Trans>Logging out…</Trans>
                    ) : (
                      <Trans>Log out</Trans>
                    ),
                    icon: <LogOut className="h-4 w-4" />,
                    disabled: isSigningOut || isDeleting,
                    onSelect: async (ctx) => {
                      await signOut();
                      ctx.close();
                    },
                  },

                  { type: "separator", key: "sep-3" },

                  {
                    type: "action",
                    key: "delete",
                    label: <Trans>Delete account…</Trans>,
                    icon: <Trash2 className="h-4 w-4" />,
                    destructive: true,
                    disabled: isSigningOut || isDeleting,
                    onSelect: (ctx) => {
                      ctx.close();
                      setDeleteOpen(true);
                    },
                  },
                ],
              },
              {
                id: "language",
                title: <Trans>Language</Trans>,
                items: [
                  {
                    type: "radio-group",
                    key: "lang-radio",
                    value: locale,
                    onValueChange: async (v, ctx) => {
                      await changeLanguage(v as AppLocale, true);
                      ctx.close();
                    },
                    options: [
                      { value: "en", label: LANGUAGE_LABEL.en },
                      { value: "fr", label: LANGUAGE_LABEL.fr },
                    ],
                  },
                ],
              },
              {
                id: "units",
                title: <Trans>Units</Trans>,
                items: [
                  {
                    type: "radio-group",
                    key: "units-radio",
                    value: unitSystem,
                    onValueChange: async (v, ctx) => {
                      await changeUnitSystem(v as UnitSystem);
                      ctx.close();
                    },
                    options: [
                      { value: "metric", label: UNIT_LABEL.metric },
                      { value: "imperial", label: UNIT_LABEL.imperial },
                    ],
                  },
                ],
              },
            ]}
          />
        </SidebarMenuItem>
      </SidebarMenu>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete your account?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                This permanently deletes your account and all associated cached
                data (Strava connection and activities). This action cannot be
                undone.
              </Trans>
            </AlertDialogDescription>

            {deleteError ? (
              <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {deleteError}
              </div>
            ) : null}
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void deleteAccount();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Trans>Deleting…</Trans>
              ) : (
                <Trans>Yes, delete my account</Trans>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
